/**
 * Dash Install Prompt Component
 *
 * PWA installation prompt for the dashboard experience (legacy `dash.*` or apex `/dashboard`).
 * Shows after first successful login with platform-specific instructions.
 * Respects user dismissal (don't show again for 7 days).
 *
 * @author AI Assistant
 * @version 2.0.0
 */
import React, { useState, useEffect } from 'react';
// import { logger } from "../../utils/logger";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Stepper, Step, StepLabel, StepContent, IconButton, useTheme, useMediaQuery, Alert, alpha } from '@mui/material';
import { Close, GetApp, PhoneIphone, DesktopWindows, MoreVert, Share, AddBox, CheckCircle } from '@mui/icons-material';
import { trackEvent } from '../../utils/analytics';
import { logger } from "../../utils/logger";
// =============================================================================
// TYPES
// =============================================================================
interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{
        outcome: 'accepted' | 'dismissed';
    }>;
}
interface DashInstallPromptProps {
    onInstalled?: () => void;
    onDismissed?: () => void;
}
// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================
const isIOS = (): boolean => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};
const isAndroid = (): boolean => {
    return /Android/.test(navigator.userAgent);
};
const isInStandaloneMode = (): boolean => {
    return (window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true);
};
const getPlatform = (): 'ios' | 'android' | 'desktop' => {
    if (isIOS())
        return 'ios';
    if (isAndroid())
        return 'android';
    return 'desktop';
};
// =============================================================================
// INSTALL PROMPT COMPONENT
// =============================================================================
export const DashInstallPrompt: React.FC<DashInstallPromptProps> = ({ onInstalled, onDismissed }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const platform = getPlatform();
    const [open, setOpen] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [isInstalled, setIsInstalled] = useState(isInStandaloneMode());
    const [activeStep, setActiveStep] = useState(0);
    // =============================================================================
    // EFFECTS
    // =============================================================================
    useEffect(() => {
        // Check if user has dismissed the prompt recently
        const lastDismissed = localStorage.getItem('dash-pwa-install-dismissed');
        if (lastDismissed) {
            const dismissedDate = new Date(lastDismissed);
            const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
            if (daysSinceDismissed < 7) {
                return; // Don't show if dismissed within last 7 days
            }
        }
        // Check if already installed
        if (isInstalled) {
            return;
        }
        // Listen for beforeinstallprompt event (Chrome, Edge)
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
            // Show prompt after user has logged in
            const hasLoggedIn = localStorage.getItem('user-logged-in');
            if (hasLoggedIn) {
                setTimeout(() => {
                    setOpen(true);
                    trackEvent('pwa_install_prompt_shown', { platform });
                }, 2000); // Show after 2 seconds
            }
        };
        // Listen for successful installation
        const handleAppInstalled = () => {
            setIsInstalled(true);
            setOpen(false);
            trackEvent('pwa_installed', { platform });
            onInstalled?.();
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);
        // For iOS, show prompt after login if not in standalone mode
        if (platform === 'ios' && !isInstalled) {
            const hasLoggedIn = localStorage.getItem('user-logged-in');
            if (hasLoggedIn) {
                setTimeout(() => {
                    setOpen(true);
                    trackEvent('pwa_install_prompt_shown', { platform: 'ios' });
                }, 2000);
            }
        }
        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, [isInstalled, platform, onInstalled]);
    // =============================================================================
    // HANDLERS
    // =============================================================================
    const handleInstall = async () => {
        if (!deferredPrompt) {
            // For iOS or browsers without native prompt
            return;
        }
        trackEvent('pwa_install_clicked', { platform });
        try {
            await deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                logger.debug('✅ PWA installation accepted');
                trackEvent('pwa_install_accepted', { platform });
            }
            else {
                logger.debug('❌ PWA installation dismissed');
                trackEvent('pwa_install_rejected', { platform });
            }
            setDeferredPrompt(null);
            setOpen(false);
        }
        catch (error) {
            logger.error('Error installing PWA:', error, {});
            trackEvent('pwa_install_error', { platform, error: String(error) });
        }
    };
    const handleClose = () => {
        setOpen(false);
        localStorage.setItem('dash-pwa-install-dismissed', new Date().toISOString());
        trackEvent('pwa_install_dismissed', { platform });
        onDismissed?.();
    };
    const handleRemindLater = () => {
        setOpen(false);
        // Don't set the long-term dismissal, allow it to show again on next login
        trackEvent('pwa_install_remind_later', { platform });
    };
    // =============================================================================
    // RENDER HELPERS
    // =============================================================================
    const renderIOSInstructions = () => {
        const steps = [
            {
                label: 'Tap the Share button',
                description: (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Share sx={{ color: theme.palette.primary.main }}/>
            <Typography variant="body2">
              Tap the Share icon at the bottom of Safari
            </Typography>
          </Box>)
            },
            {
                label: 'Tap "Add to Home Screen"',
                description: (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddBox sx={{ color: theme.palette.primary.main }}/>
            <Typography variant="body2">
              Scroll down and tap "Add to Home Screen"
            </Typography>
          </Box>)
            },
            {
                label: 'Tap "Add"',
                description: (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ color: theme.palette.success.main }}/>
            <Typography variant="body2">
              Confirm by tapping "Add" in the top right
            </Typography>
          </Box>)
            }
        ];
        return (<Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (<Step key={step.label}>
            <StepLabel>{step.label}</StepLabel>
            <StepContent>
              {step.description}
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={() => setActiveStep(index + 1)} size="small" disabled={index === steps.length - 1}>
                  {index === steps.length - 1 ? 'Done' : 'Next'}
                </Button>
              </Box>
            </StepContent>
          </Step>))}
      </Stepper>);
    };
    const renderContent = () => {
        switch (platform) {
            case 'ios':
                return (<>
            <Alert severity="info" sx={{ mb: 2 }}>
              Install APT Dashboard for quick access from your home screen
            </Alert>
            {renderIOSInstructions()}
          </>);
            case 'android':
                return (<>
            <Alert severity="info" sx={{ mb: 2 }} icon={<PhoneIphone />}>
              Install APT Dashboard for a native app experience
            </Alert>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <GetApp sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2 }}/>
              <Typography variant="body1" gutterBottom>
                Tap the button below to install APT Dashboard on your device
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Get fast access, offline support, and push notifications
              </Typography>
            </Box>
          </>);
            case 'desktop':
                return (<>
            <Alert severity="info" sx={{ mb: 2 }} icon={<DesktopWindows />}>
              Install APT Dashboard for quick access from your desktop
            </Alert>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <GetApp sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2 }}/>
              <Typography variant="body1" gutterBottom>
                Click the button below to install APT Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Works like a native app with offline support and notifications
              </Typography>
            </Box>
          </>);
        }
    };
    // =============================================================================
    // RENDER
    // =============================================================================
    if (isInstalled) {
        return null;
    }
    return (<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{
            sx: {
                borderRadius: isMobile ? 0 : 2,
                background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.1)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`
                    : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`
            }
        }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GetApp sx={{ color: theme.palette.primary.main }}/>
          <Typography variant="h6">Install APT Dashboard</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {renderContent()}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleRemindLater} color="inherit">
          Remind Me Later
        </Button>
        <Button onClick={handleClose} color="inherit">
          Not Now
        </Button>
        {platform !== 'ios' && deferredPrompt && (<Button onClick={handleInstall} variant="contained" startIcon={<GetApp />}>
            Install
          </Button>)}
      </DialogActions>
    </Dialog>);
};
export default DashInstallPrompt;
